import React, { useContext, useEffect, useRef, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import Config from "../utils/Config";
import { formatDateTime } from "../utils/functions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import "./SupportRequests.css";
import SupportTicketActions from "./SupportTicketActions";

export default function SupportRequests({ isAdmin }) {
  const { user } = useContext(AuthContext);
  const userId = user?.id || user?._id;
  const allowed = userId && isAdmin && (user.userLevel == null || user.userLevel >= (Config?.USERS?.ADMIN_USER_LEVEL ?? 3));
  return allowed ? <SupportRequestsInbox key={String(userId)} /> : null;
}

function SupportRequestsInbox() {
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  useEffect(() => {
    if (searchText.trim() === search) return;
    const timer = setTimeout(() => { setSearch(searchText.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchText, search]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setResult(null);
    setError("");
    setSelectedId(null);
    (async () => {
      try {
        const token = localStorage.getItem("authToken") || sessionStorage.getItem("authToken");
        if (!token) throw new Error("Please sign in again to view support requests.");
        const response = await fetch(Config.API.API_BASE_URL + "/api/support/requests?page=" + page + "&pageSize=20&q=" + encodeURIComponent(search) + "&status=" + status, {
          headers: { Authorization: "Bearer " + token }, signal: controller.signal,
        });
        if (!response.ok) throw new Error(response.status === 401 || response.status === 403
          ? "Administrator authorization is required to view support requests."
          : "Unable to load support requests. Please try again.");
        const data = await response.json();
        if (controller.signal.aborted) return;
        if (!Array.isArray(data.requests) || !Number.isSafeInteger(data.total) || data.total < 0 || data.page !== page || data.pageSize !== 20 ||
            data.requests.some(item => !item || typeof item._id !== "string" || typeof item.subject !== "string" || typeof item.message !== "string")) {
          throw new Error("Unable to read the support requests response.");
        }
        const lastPage = Math.max(1, Math.ceil(data.total / 20));
        if (page > lastPage) { setPage(lastPage); return; }
        // Preserve the endpoint's newest-first order; pagination is server-side.
        setResult(data);
      } catch (failure) {
        if (!controller.signal.aborted) setError(failure instanceof TypeError
          ? "Unable to connect. Check your connection and try again."
          : failure.message || "Unable to load support requests.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [page, reload, search, status]);
  const pageCount = Math.max(1, Math.ceil((result?.total || 0) / 20));
  const dateLabel = value => value && Number.isFinite(new Date(value).getTime()) ? formatDateTime(value) : "Unavailable";
  const selected = result?.requests.find(item => item._id === selectedId);
  const sender = (item, showNote = false) => <><span>{typeof item.senderEmail === "string" && item.senderEmail.trim() ? item.senderEmail : "Sender unavailable"}</span>{showNote && item.senderEmailSource === "current-account" && <small>Current account email</small>}</>;
  return <div className="login-container login-container-dashboard"><div className="login-box support-requests">
    <h2 className="dashboard-title"><span className="titleIcon"><FontAwesomeIcon icon={faEnvelope} /></span>&nbsp;&nbsp;Support Requests</h2>
    {!selected && <div className="support-inbox-filters">
      <input type="search" aria-label="Search support requests" placeholder="Search email, subject, or message..." maxLength={100} value={searchText} onChange={event => setSearchText(event.target.value)} />
      <select aria-label="Support request status" value={status} onChange={event => { setStatus(event.target.value); setPage(1); }}><option value="">All</option><option value="OPEN">Open</option><option value="CLOSED">Closed</option></select>
    </div>}
    <details className="support-email-disclosure"><summary>Support Email Settings</summary><SupportEmailSettings /></details>
    {loading ? <p role="status">Loading support requests…</p> : error ? <div role="alert"><p>{error}</p><button type="button" onClick={() => setReload(value => value + 1)}>Try Again</button></div> : result && <>
      {selected ? <article className="support-request-detail">
        <button type="button" onClick={() => setSelectedId(null)}>← Back to Inbox</button>
        <h3>{selected.subject}</h3>
        <dl><dt>From</dt><dd>{sender(selected, true)}</dd>
          <dt>Submitted</dt><dd>{dateLabel(selected.createdAt)}</dd>
          <dt>Status</dt><dd>{typeof selected.status === "string" ? selected.status : "Unavailable"}</dd>
          <dt>Reference</dt><dd>{selected._id}</dd></dl>
        <p className="support-message-body">{selected.message}</p>
        <SupportTicketActions key={selected._id} initialTicket={selected} onUpdate={fields => setResult(current => ({ ...current, requests: current.requests.map(item => item._id === selected._id ? { ...item, ...fields } : item) }))} />
        {typeof selected.userId === "string" && <details><summary>Account information</summary><p>Account ID: {selected.userId}</p></details>}
      </article> : !result.requests.length ? <p>{search || status ? "No matching support requests" : "No support requests have been submitted."}</p> : <>
        <div className="support-inbox-columns" aria-hidden="true"><span>From</span><span>Subject</span><span>Last activity</span><span>Status</span></div>
        <ul className="support-request-list">{result.requests.map(item => <li key={item._id}>
          <button type="button" className="support-inbox-row" onClick={() => setSelectedId(item._id)}>
            <span className="support-inbox-from">{sender(item)}</span>
            <span className="support-inbox-content"><strong>{item.subject}</strong><span className="support-inbox-preview">{item.message.replace(/\s+/g, " ").trim()}</span></span>
            <span className="support-inbox-date">{dateLabel(item.lastActivityAt)}</span>
            <span className="support-inbox-status">{typeof item.status === "string" ? item.status : "Unavailable"}</span>
          </button>
        </li>)}</ul>
      </>}
      {!selected && result.total > 20 && <nav className="support-request-pagination" aria-label="Support requests pagination">
        <button type="button" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>Previous</button>
        <span aria-live="polite">Page {page} of {pageCount}</span>
        <button type="button" disabled={page >= pageCount} onClick={() => setPage(value => value + 1)}>Next</button>
      </nav>}
    </>}
  </div></div>;
}

function SupportEmailSettings() {
  const [email, setEmail] = useState("");
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [reload, setReload] = useState(0);
  const active = useRef(null);
  useEffect(() => {
    const controller = new AbortController();
    active.current = controller;
    setReady(false);
    setFeedback(null);
    (async () => {
      try {
        const response = await fetch(Config.API.API_BASE_URL + "/api/support/settings", {
          headers: { Authorization: "Bearer " + (localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || "") }, signal: controller.signal,
        });
        if (!response.ok) throw new Error();
        const data = await response.json();
        if (data.supportEmail !== null && typeof data.supportEmail !== "string") throw new Error();
        if (!controller.signal.aborted) { setEmail(data.supportEmail || ""); setReady(true); }
      } catch {
        if (!controller.signal.aborted) setFeedback({ error: true, text: "Unable to load Support Email settings. Administrator authorization is required." });
      } finally {
        if (active.current === controller) active.current = null;
      }
    })();
    return () => { controller.abort(); active.current?.abort(); };
  }, [reload]);
  const save = async (event, clear = false) => {
    event.preventDefault();
    if (!ready || active.current) return;
    const supportEmail = clear ? null : email.trim() || null;
    if (!clear && (email.length > 254 || /[\r\n]/.test(email) || (supportEmail &&
        (!/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(supportEmail))))) {
      setFeedback({ error: true, text: "Enter one valid email address (maximum 254 characters)." });
      return;
    }
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    setFeedback(null);
    try {
      const response = await fetch(Config.API.API_BASE_URL + "/api/support/settings", {
        method: "PUT", headers: { "Content-Type": "application/json", Authorization: "Bearer " + (localStorage.getItem("authToken") || sessionStorage.getItem("authToken") || "") },
        body: JSON.stringify({ supportEmail }), signal: controller.signal,
      });
      if (!response.ok) throw new Error();
      const data = await response.json();
      if (data.supportEmail !== supportEmail) throw new Error();
      if (!controller.signal.aborted) {
        setEmail(data.supportEmail || "");
        setFeedback({ error: false, text: supportEmail ? "Support Email saved." : "Address cleared. Email notifications are disabled." });
      }
    } catch {
      if (!controller.signal.aborted) setFeedback({ error: true, text: "Unable to save Support Email. Check the address, authorization, and connection, then try again." });
    } finally {
      if (active.current === controller) active.current = null;
      if (!controller.signal.aborted) setBusy(false);
    }
  };
  return <section className="support-email-settings" aria-label="Support Email">
    <h3>Support Email</h3>
    <p>Empty address means email notifications are disabled.</p>
    {!ready && !feedback && <p role="status">Loading Support Email…</p>}
    {feedback && <p role={feedback.error ? "alert" : "status"}>{feedback.text}</p>}
    {!ready && feedback && <button type="button" onClick={() => setReload(value => value + 1)}>Reload Settings</button>}
    <form onSubmit={save} noValidate>
      <label htmlFor="support-email-address">Support Email Address</label>
      <input id="support-email-address" type="email" maxLength={254} value={email} disabled={!ready || busy} onChange={event => setEmail(event.target.value)} />
      <div><button type="submit" disabled={!ready || busy}>{busy ? "Saving…" : "Save"}</button>
        <button type="button" disabled={!ready || busy} onClick={event => save(event, true)}>Clear Address</button></div>
    </form>
  </section>;
}
